/**
 * @file pages/Chat.js
 * @description Page de chat global en temps réel avec Socket.io
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { useAuth } from '../context/AuthContext';
import { API } from '../context/AuthContext';

const SOCKET_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';

const Chat = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [connected, setConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [typingUsers, setTypingUsers] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const socketRef = useRef(null);
  const bottomRef = useRef(null);
  const typingTimerRef = useRef(null);
  const isTypingRef = useRef(false);

  // Redirection si non connecté
  useEffect(() => {
    if (!isAuthenticated) navigate('/login');
  }, [isAuthenticated, navigate]);

  // Scroll automatique vers le bas
  const scrollToBottom = useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Charger l'historique des messages
  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const { data } = await API.get('/chat/history');
        setMessages(data.messages);
      } catch (err) {
        console.error('Erreur historique:', err);
      } finally {
        setLoadingHistory(false);
      }
    };
    if (isAuthenticated) fetchHistory();
  }, [isAuthenticated]);

  // Connexion Socket.io
  useEffect(() => {
    if (!isAuthenticated) return;

    const token = localStorage.getItem('token');
    const socket = io(SOCKET_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
    });

    socket.on('disconnect', () => {
      setConnected(false);
    });

    // Nouveau message reçu
    socket.on('message:new', (message) => {
      setMessages(prev => [...prev, message]);
    });

    // Utilisateur rejoint
    socket.on('user:joined', ({ userName, userId }) => {
      if (userId !== user?._id) {
        setOnlineUsers(prev => {
          if (prev.find(u => u.userId === userId)) return prev;
          return [...prev, { userName, userId }];
        });
        // Notification système
        setMessages(prev => [...prev, {
          _id: `sys-${Date.now()}`,
          system: true,
          content: `${userName} a rejoint le chat`,
          createdAt: new Date().toISOString(),
        }]);
      }
    });

    // Utilisateur parti
    socket.on('user:left', ({ userName, userId }) => {
      setOnlineUsers(prev => prev.filter(u => u.userId !== userId));
      setMessages(prev => [...prev, {
        _id: `sys-${Date.now()}`,
        system: true,
        content: `${userName} a quitté le chat`,
        createdAt: new Date().toISOString(),
      }]);
    });

    // Indicateur de frappe
    socket.on('typing:update', ({ userName, isTyping }) => {
      setTypingUsers(prev => {
        if (isTyping) {
          if (prev.includes(userName)) return prev;
          return [...prev, userName];
        }
        return prev.filter(u => u !== userName);
      });
    });

    socket.on('connect_error', (err) => {
      console.error('Erreur connexion socket:', err.message);
      setConnected(false);
    });

    return () => {
      socket.disconnect();
    };
  }, [isAuthenticated, user]);

  // Gestion de la frappe
  const handleInputChange = (e) => {
    setInput(e.target.value);

    if (!isTypingRef.current) {
      isTypingRef.current = true;
      socketRef.current?.emit('typing:start');
    }

    clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => {
      isTypingRef.current = false;
      socketRef.current?.emit('typing:stop');
    }, 1500);
  };

  // Envoi d'un message
  const handleSend = () => {
    const content = input.trim();
    if (!content || !connected) return;

    socketRef.current?.emit('message:send', content);
    setInput('');

    // Arrêter l'indicateur de frappe
    clearTimeout(typingTimerRef.current);
    isTypingRef.current = false;
    socketRef.current?.emit('typing:stop');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (dateStr) => {
    return new Date(dateStr).toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return "Aujourd'hui";
    if (date.toDateString() === yesterday.toDateString()) return 'Hier';
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });
  };

  // Grouper les messages par date
  const groupedMessages = messages.reduce((groups, message) => {
    const date = formatDate(message.createdAt);
    if (!groups[date]) groups[date] = [];
    groups[date].push(message);
    return groups;
  }, {});

  const getInitials = (name) => name?.charAt(0).toUpperCase() || '?';

  const getAvatarColor = (name) => {
    const colors = ['#A98667', '#6b8cad', '#7a9e7e', '#9b7aa0', '#c27b5a', '#5a8fa3'];
    const index = (name?.charCodeAt(0) || 0) % colors.length;
    return colors[index];
  };

  return (
    <>
      <style>{`
        .chat-page {
          height: 100vh;
          display: flex;
          flex-direction: column;
          padding-top: 70px;
          background: linear-gradient(180deg, #1F2A44 0%, #141d30 100%);
        }
        .chat-layout {
          display: flex;
          flex: 1;
          overflow: hidden;
          max-width: 1300px;
          width: 100%;
          margin: 0 auto;
          padding: 24px 24px 0;
          gap: 24px;
        }
        /* ── Sidebar ── */
        .chat-sidebar {
          width: 260px;
          flex-shrink: 0;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        .sidebar-card {
          background: rgba(31,42,68,0.5);
          border: 1px solid rgba(169,134,103,0.15);
          border-radius: 16px;
          padding: 20px;
        }
        .sidebar-title {
          font-size: 0.75rem;
          font-weight: 700;
          letter-spacing: 2px;
          text-transform: uppercase;
          color: #A98667;
          margin-bottom: 14px;
        }
        .chat-status {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.875rem;
        }
        .status-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          flex-shrink: 0;
        }
        .status-dot.online { background: #27ae60; box-shadow: 0 0 6px rgba(39,174,96,0.5); }
        .status-dot.offline { background: #e74c3c; }
        .status-text { color: rgba(240,234,226,0.7); }
        .online-user {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 8px 0;
          border-bottom: 1px solid rgba(169,134,103,0.07);
        }
        .online-user:last-child { border-bottom: none; }
        .user-avatar-sm {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.8rem;
          font-weight: 700;
          color: #FFFFFF;
          flex-shrink: 0;
        }
        .user-name-sm {
          font-size: 0.875rem;
          color: rgba(240,234,226,0.8);
        }
        .user-name-sm.me { color: #A98667; font-weight: 600; }
        .rules-list {
          list-style: none;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .rules-list li {
          font-size: 0.8rem;
          color: rgba(240,234,226,0.5);
          display: flex;
          align-items: flex-start;
          gap: 6px;
          line-height: 1.4;
        }
        .rules-list li::before {
          content: '✦';
          color: #A98667;
          font-size: 0.65rem;
          margin-top: 2px;
          flex-shrink: 0;
        }
        /* ── Main chat ── */
        .chat-main {
          flex: 1;
          display: flex;
          flex-direction: column;
          min-width: 0;
          background: rgba(31,42,68,0.4);
          border: 1px solid rgba(169,134,103,0.15);
          border-radius: 20px 20px 0 0;
          overflow: hidden;
        }
        .chat-header {
          padding: 18px 24px;
          border-bottom: 1px solid rgba(169,134,103,0.12);
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: rgba(31,42,68,0.6);
          flex-shrink: 0;
        }
        .chat-header-left {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .chat-header-icon {
          width: 40px;
          height: 40px;
          background: rgba(169,134,103,0.15);
          border: 1px solid rgba(169,134,103,0.25);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.1rem;
        }
        .chat-header-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 1.2rem;
          color: #FFFFFF;
          font-weight: 600;
        }
        .chat-header-sub {
          font-size: 0.78rem;
          color: rgba(240,234,226,0.45);
          margin-top: 1px;
        }
        /* ── Messages area ── */
        .chat-messages {
          flex: 1;
          overflow-y: auto;
          padding: 24px;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .chat-messages::-webkit-scrollbar { width: 4px; }
        .chat-messages::-webkit-scrollbar-thumb { background: rgba(169,134,103,0.2); border-radius: 2px; }
        .date-divider {
          display: flex;
          align-items: center;
          gap: 12px;
          margin: 16px 0 8px;
        }
        .date-divider::before, .date-divider::after {
          content: '';
          flex: 1;
          height: 1px;
          background: rgba(169,134,103,0.1);
        }
        .date-divider-text {
          font-size: 0.72rem;
          font-weight: 600;
          letter-spacing: 1px;
          color: rgba(240,234,226,0.3);
          text-transform: uppercase;
          white-space: nowrap;
        }
        .system-message {
          text-align: center;
          font-size: 0.78rem;
          color: rgba(240,234,226,0.3);
          font-style: italic;
          padding: 4px 0;
        }
        .message-row {
          display: flex;
          align-items: flex-end;
          gap: 10px;
          margin-bottom: 4px;
        }
        .message-row.mine {
          flex-direction: row-reverse;
        }
        .message-avatar {
          width: 34px;
          height: 34px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.8rem;
          font-weight: 700;
          color: #FFFFFF;
          flex-shrink: 0;
          margin-bottom: 2px;
        }
        .message-avatar.hidden { visibility: hidden; }
        .message-body { max-width: 65%; display: flex; flex-direction: column; }
        .message-row.mine .message-body { align-items: flex-end; }
        .message-sender {
          font-size: 0.72rem;
          font-weight: 600;
          color: #A98667;
          margin-bottom: 4px;
          padding: 0 4px;
        }
        .message-bubble {
          padding: 10px 14px;
          border-radius: 16px;
          font-size: 0.9rem;
          line-height: 1.5;
          word-break: break-word;
          position: relative;
        }
        .message-row:not(.mine) .message-bubble {
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(169,134,103,0.1);
          color: rgba(240,234,226,0.9);
          border-bottom-left-radius: 4px;
        }
        .message-row.mine .message-bubble {
          background: linear-gradient(135deg, #A98667, #c4a07d);
          color: #FFFFFF;
          border-bottom-right-radius: 4px;
        }
        .message-time {
          font-size: 0.68rem;
          color: rgba(240,234,226,0.3);
          margin-top: 4px;
          padding: 0 4px;
        }
        /* ── Typing indicator ── */
        .typing-indicator {
          padding: 8px 24px;
          font-size: 0.78rem;
          color: rgba(240,234,226,0.4);
          font-style: italic;
          min-height: 28px;
          flex-shrink: 0;
        }
        .typing-dots {
          display: inline-flex;
          gap: 3px;
          margin-left: 4px;
          vertical-align: middle;
        }
        .typing-dot {
          width: 4px;
          height: 4px;
          border-radius: 50%;
          background: #A98667;
          animation: typingBounce 1.2s infinite;
        }
        .typing-dot:nth-child(2) { animation-delay: 0.2s; }
        .typing-dot:nth-child(3) { animation-delay: 0.4s; }
        @keyframes typingBounce {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
          30% { transform: translateY(-4px); opacity: 1; }
        }
        /* ── Input area ── */
        .chat-input-area {
          padding: 16px 20px;
          border-top: 1px solid rgba(169,134,103,0.12);
          background: rgba(31,42,68,0.6);
          display: flex;
          gap: 12px;
          align-items: flex-end;
          flex-shrink: 0;
        }
        .chat-input-wrapper {
          flex: 1;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(169,134,103,0.2);
          border-radius: 14px;
          display: flex;
          align-items: flex-end;
          padding: 4px 4px 4px 16px;
          transition: border-color 0.2s;
        }
        .chat-input-wrapper:focus-within {
          border-color: rgba(169,134,103,0.5);
          background: rgba(169,134,103,0.05);
        }
        .chat-input {
          flex: 1;
          background: none;
          border: none;
          outline: none;
          color: #FFFFFF;
          font-size: 0.9rem;
          font-family: 'Jost', sans-serif;
          resize: none;
          max-height: 100px;
          padding: 10px 0;
          line-height: 1.5;
        }
        .chat-input::placeholder { color: rgba(240,234,226,0.25); }
        .char-count {
          font-size: 0.7rem;
          color: rgba(240,234,226,0.25);
          padding: 8px;
          align-self: flex-end;
          white-space: nowrap;
        }
        .send-btn {
          width: 44px;
          height: 44px;
          background: linear-gradient(135deg, #A98667, #c4a07d);
          border: none;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
          flex-shrink: 0;
          font-size: 1.1rem;
        }
        .send-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(169,134,103,0.4);
        }
        .send-btn:disabled { opacity: 0.4; cursor: not-allowed; }
        /* ── Loading ── */
        .chat-loading {
          display: flex;
          align-items: center;
          justify-content: center;
          flex: 1;
          color: rgba(240,234,226,0.4);
          font-size: 0.9rem;
          gap: 10px;
        }
        /* ── Responsive ── */
        @media (max-width: 900px) {
          .chat-sidebar { display: none; }
          .chat-layout { padding: 16px 16px 0; }
        }
        @media (max-width: 600px) {
          .chat-layout { padding: 8px 8px 0; }
          .chat-messages { padding: 16px; }
          .message-body { max-width: 80%; }
        }
      `}</style>

      <div className="chat-page">
        <div className="chat-layout">

          {/* ── Sidebar ── */}
          <aside className="chat-sidebar">
            {/* Statut */}
            <div className="sidebar-card">
              <p className="sidebar-title">Statut</p>
              <div className="chat-status">
                <div className={`status-dot ${connected ? 'online' : 'offline'}`} />
                <span className="status-text">
                  {connected ? 'Connecté en temps réel' : 'Reconnexion...'}
                </span>
              </div>
            </div>

            {/* Utilisateurs en ligne */}
            <div className="sidebar-card" style={{ flex: 1 }}>
              <p className="sidebar-title">En ligne ({onlineUsers.length + 1})</p>

              {/* Moi */}
              <div className="online-user">
                <div className="user-avatar-sm" style={{ background: getAvatarColor(user?.name) }}>
                  {getInitials(user?.name)}
                </div>
                <span className="user-name-sm me">{user?.name} (vous)</span>
              </div>

              {onlineUsers.map(u => (
                <div className="online-user" key={u.userId}>
                  <div className="user-avatar-sm" style={{ background: getAvatarColor(u.userName) }}>
                    {getInitials(u.userName)}
                  </div>
                  <span className="user-name-sm">{u.userName}</span>
                </div>
              ))}
            </div>

            {/* Règles */}
            <div className="sidebar-card">
              <p className="sidebar-title">Bonne conduite</p>
              <ul className="rules-list">
                <li>Respectez tous les membres</li>
                <li>Restez dans un esprit spirituel et bienveillant</li>
                <li>Pas de contenus offensants</li>
                <li>Max 500 caractères par message</li>
              </ul>
            </div>
          </aside>

          {/* ── Chat principal ── */}
          <div className="chat-main">
            {/* Header */}
            <div className="chat-header">
              <div className="chat-header-left">
                <div className="chat-header-icon">💬</div>
                <div>
                  <p className="chat-header-title">Chat Communautaire</p>
                  <p className="chat-header-sub">Discussion ouverte à tous les membres connectés</p>
                </div>
              </div>
              <div className="chat-status">
                <div className={`status-dot ${connected ? 'online' : 'offline'}`} />
                <span style={{ fontSize: '0.8rem', color: 'rgba(240,234,226,0.5)' }}>
                  {connected ? 'En ligne' : 'Hors ligne'}
                </span>
              </div>
            </div>

            {/* Messages */}
            <div className="chat-messages">
              {loadingHistory ? (
                <div className="chat-loading">
                  <div className="loader" style={{ width: 24, height: 24, borderWidth: 2 }} />
                  Chargement de l'historique...
                </div>
              ) : (
                <>
                  {Object.entries(groupedMessages).map(([date, msgs]) => (
                    <React.Fragment key={date}>
                      <div className="date-divider">
                        <span className="date-divider-text">{date}</span>
                      </div>
                      {msgs.map((msg, i) => {
                        if (msg.system) {
                          return (
                            <div className="system-message" key={msg._id}>
                              {msg.content}
                            </div>
                          );
                        }

                        const isMe = msg.userId === user?._id || msg.userName === user?.name;
                        const nextMsg = msgs[i + 1];
                        const showAvatar = !nextMsg || nextMsg.system || nextMsg.userName !== msg.userName;

                        return (
                          <div key={msg._id} className={`message-row ${isMe ? 'mine' : ''}`}>
                            <div
                              className={`message-avatar ${!showAvatar ? 'hidden' : ''}`}
                              style={{ background: getAvatarColor(msg.userName) }}
                            >
                              {getInitials(msg.userName)}
                            </div>
                            <div className="message-body">
                              {!isMe && showAvatar && (
                                <span className="message-sender">{msg.userName}</span>
                              )}
                              <div className="message-bubble">{msg.content}</div>
                              {showAvatar && (
                                <span className="message-time">{formatTime(msg.createdAt)}</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </React.Fragment>
                  ))}

                  {messages.length === 0 && !loadingHistory && (
                    <div style={{ textAlign: 'center', padding: '60px 20px', color: 'rgba(240,234,226,0.3)' }}>
                      <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>💬</div>
                      <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.2rem', marginBottom: 8, color: 'rgba(240,234,226,0.5)' }}>
                        Aucun message pour l'instant
                      </p>
                      <p style={{ fontSize: '0.875rem' }}>Soyez le premier à démarrer la discussion !</p>
                    </div>
                  )}

                  <div ref={bottomRef} />
                </>
              )}
            </div>

            {/* Indicateur de frappe */}
            <div className="typing-indicator">
              {typingUsers.length > 0 && (
                <>
                  {typingUsers.join(', ')} {typingUsers.length === 1 ? 'écrit' : 'écrivent'}
                  <span className="typing-dots">
                    <span className="typing-dot" />
                    <span className="typing-dot" />
                    <span className="typing-dot" />
                  </span>
                </>
              )}
            </div>

            {/* Zone de saisie */}
            <div className="chat-input-area">
              <div
                className="user-avatar-sm"
                style={{ background: getAvatarColor(user?.name), width: 36, height: 36, borderRadius: '50%', flexShrink: 0 }}
              >
                {getInitials(user?.name)}
              </div>
              <div className="chat-input-wrapper">
                <textarea
                  className="chat-input"
                  placeholder="Écrivez votre message... (Entrée pour envoyer)"
                  value={input}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  maxLength={500}
                  rows={1}
                  disabled={!connected}
                />
                <span className="char-count">{input.length}/500</span>
              </div>
              <button
                className="send-btn"
                onClick={handleSend}
                disabled={!input.trim() || !connected}
                title="Envoyer"
              >
                ➤
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Chat;